"use client";

import type { FC } from "react";
import { useMemo } from "react";

import { StandardCheckoutApp } from "@jti/checkout/StandardCheckout";
import type { CheckoutConfiguration } from "@jti/commerce";
import { Container } from "@jti/core-components/components/Container";

import { useCheckoutNavigationHandler } from "@/hooks/useCheckoutNavigationHandler";

import type { SobQuestionsBuilderReferenceResponse } from "../SobQuiz/SobQuiz";
import { transformSobData } from "../SobQuiz/utils/transformSobData";
import { useSurveyScriptUrl } from "../Survey/hooks/useSurveyScriptUrl";

import type { checkoutInputs } from "./Checkout.builder";

type SobBase = BuilderComponentProps<typeof checkoutInputs>["sob"];

type SobQuestionsType = SobBase & {
  questions?: SobQuestionsBuilderReferenceResponse;
};

export type CheckoutProps = Override<BuilderComponentProps<typeof checkoutInputs>, { sob: SobQuestionsType }>;

const CART_OVERVIEW_TIER_MAX_BENEFITS = 4;

export const Checkout: FC<CheckoutProps> = ({
  payOnDelivery,
  zeroValueOrder,
  loyalty,
  deliveryOptions,
  discountDisclaimer,
  ageVerificationPopup,
  contactConfig,
  failedPaymentFlow,
  taxInfo,
  orderSummaryConfig,
  consentsConfig,
  consentReminderPopup,
  subscriptionTermsPlacement,
  enableSobQuiz,
  sob,
  survey,
  cartOverviewTier,
  cartOverviewTierV2Enabled,
}) => {
  const handleNavigate = useCheckoutNavigationHandler();
  const sobQuestions = useMemo(() => {
    if (!enableSobQuiz) {
      return undefined;
    }

    return transformSobData(sob?.questions?.value?.data?.questions, sob?.uuid);
  }, [enableSobQuiz, sob?.questions?.value?.data?.questions, sob?.uuid]);

  const sobData: CheckoutConfiguration["sob"] =
    enableSobQuiz && sobQuestions
      ? {
          data: {
            quizTransitions: sobQuestions?.quizTransitions,
            answerDimensions: sobQuestions?.answerDimensions,
            questionDimensions: sobQuestions?.questionDimensions,
            uuid: sobQuestions?.uuid,
            consentCaptureQuestion: sob?.consentCaptureQuestion,
            questionScreen: sob?.questionScreen,
            finalScreen: sob?.finalScreen,
            type: sob?.type || "SOURCE_OF_BUSINESS",
          },
        }
      : undefined;

  const { identifier, domain } = survey || {};
  const scriptUrl = useSurveyScriptUrl(identifier, domain);
  const surveyObj: CheckoutConfiguration["survey"] = scriptUrl ? { scriptUrl } : undefined;

  // Creation of this model was necessary to match builder inputs with the AEM CheckoutConfiguration
  // TODO: https://jira.jtiweb.co.uk/browse/DESP-84044
  const checkoutAppConfiguration: CheckoutConfiguration = {
    payOnDelivery: {
      ...payOnDelivery,
      paymentMethod: payOnDelivery?.paymentMethod || "",
    },
    zeroValueOrder: {
      ...zeroValueOrder,
      userNotification: zeroValueOrder?.userNotification || "",
    },
    loyalty: {
      ...loyalty,
      learnMoreUrl: loyalty?.learnMoreUrl || "",
      orderConfirmationLoyaltyUrl: loyalty?.orderConfirmationLoyaltyUrl || "",
      redemptionTabSymbol: loyalty?.redemptionTabSymbol || "",
      showMgm: !!loyalty?.showMgm,
      reminderDialogSecondaryButtonLink: loyalty?.reminderDialogSecondaryButtonLink || "",
      reminderDialogSecondaryButtonText: loyalty?.reminderDialogSecondaryButtonText || "",
      reminderDialogTitle: loyalty?.reminderDialogTitle || "",
      reminderDialogText: loyalty?.reminderDialogText || "",
      reminderDialogSubtitle: loyalty?.reminderDialogSubtitle || "",
      showReminderDialog: !!loyalty?.showReminderDialog,
      reminderDialogSKUs: loyalty?.reminderDialogSKUs || "",
    },
    cartOverviewTier: {
      enabled: !!cartOverviewTierV2Enabled,
      title: cartOverviewTier?.title,
      descriptionCollapsed: cartOverviewTier?.descriptionCollapsed,
      descriptionExpanded: cartOverviewTier?.descriptionExpanded,
      pointsLabel: cartOverviewTier?.pointsLabel || "Points",
      collectMessage: cartOverviewTier?.collectMessage || "Collect {points} points on this order!",
      knowMoreLabel: cartOverviewTier?.knowMoreLabel,
      knowMoreUrl: cartOverviewTier?.knowMoreUrl,
      statusMessages: {
        statusMessageUpgrade: cartOverviewTier?.statusMessages?.statusMessageUpgrade || "",
        statusMessageRetention: cartOverviewTier?.statusMessages?.statusMessageRetention || "",
        statusMessageDowngrade: cartOverviewTier?.statusMessages?.statusMessageDowngrade || "",
        statusMessageDowngradeRetained: cartOverviewTier?.statusMessages?.statusMessageDowngradeRetained || "",
        statusMessageMax: cartOverviewTier?.statusMessages?.statusMessageMax || "",
      },
      showBenefits: !!cartOverviewTier?.showBenefits,
      goldBenefits: {
        title: cartOverviewTier?.goldBenefits?.title,
        items: cartOverviewTier?.goldBenefits?.items?.slice(0, CART_OVERVIEW_TIER_MAX_BENEFITS),
      },
      blackBenefits: {
        title: cartOverviewTier?.blackBenefits?.title,
        items: cartOverviewTier?.blackBenefits?.items?.slice(0, CART_OVERVIEW_TIER_MAX_BENEFITS),
      },
    },
    deliveryOptions: deliveryOptions
      ? deliveryOptions.map((deliveryOption) => ({
          ...deliveryOption,
          defaultDelivery: !!deliveryOption.defaultDelivery,
        }))
      : [],
    discountDisclaimer: {
      ...discountDisclaimer,
      enabled: !!discountDisclaimer?.enabled,
      enabledWhenZeroValueDeposit: !!discountDisclaimer?.enabledWhenZeroValueDeposit,
      disclaimerMessage: discountDisclaimer?.disclaimerMessage || "",
      warningMessage: discountDisclaimer?.warningMessage || "",
    },
    ageVerificationMethodImages: {},
    ageVerificationPopup: ageVerificationPopup
      ? {
          ...ageVerificationPopup,
          description: ageVerificationPopup?.description || "",
          popupMode: !!ageVerificationPopup?.popupMode,
          successMessage: ageVerificationPopup?.successMessage || "",
        }
      : undefined,
    contactConfig,
    orderSummaryConfig,
    failedPaymentFlow: {
      message: failedPaymentFlow?.message || "",
      retryPaymentButtonLabel: failedPaymentFlow?.retryPaymentButtonLabel || "",
      title: failedPaymentFlow?.title || "",
    },
    taxInfo: {
      ...taxInfo,
      message: taxInfo?.message || "",
      visible: !!taxInfo?.visible,
    },
    consentsConfig,
    consentReminderPopup,
    subscriptionTermsPlacement,
    sob: sobData,
    survey: surveyObj,
  };
  return (
    // This is a temporary workaround till adapter team removes dependency to the AEM markup
    // And to avoid manual fixes that can be removed by people with less permissions than "Developer" role
    <div className="root responsivegrid">
      <Container>
        <StandardCheckoutApp data={checkoutAppConfiguration} onNavigate={handleNavigate} />
      </Container>
    </div>
  );
};
